import {
  LOGIST_TITLE_TYPE_LABELS,
  LOGIST_TITLE_STATUS_LABELS,
  type LogistEtrnEvidence,
  type LogistEtrnTitle,
} from '@entities/logistics';
import {
  SubCard,
  SubSectionTitle,
  InlineBtn,
  SubCardSubtitle,
  EvidenceBlock,
  EvidenceTitle,
  EvidenceContent,
  EvidenceMonoRow,
  ActionRow,
  SmallBtn,
  EmptyState,
  Table,
  Th,
  Td,
  Badge,
  Mono,
} from './styles';

interface LogistEtrnTitlesCardProps {
  titlesFor: string;
  evidence: LogistEtrnEvidence | null | undefined;
  titles: LogistEtrnTitle[] | undefined;
  titlesLoading: boolean;
  archiving: boolean;
  can_write: boolean;
  enabled: boolean;
  onClose: () => void;
  onArchive: (id: string) => void;
}

export function LogistEtrnTitlesCard({
  titlesFor,
  evidence,
  titles,
  titlesLoading,
  archiving,
  can_write,
  enabled,
  onClose,
  onArchive,
}: LogistEtrnTitlesCardProps) {
  return (
    <SubCard>
      <SubSectionTitle>
        Части документа{' '}
        <InlineBtn onClick={onClose}>
          Закрыть
        </InlineBtn>
      </SubSectionTitle>
      <SubCardSubtitle>
        Накладная состоит из нескольких частей: каждую подписывает своя сторона —
        отправитель, перевозчик, получатель. Пока не подписаны все обязательные части,
        перевозка не закрыта. Части, которых здесь нет, ещё не созданы у оператора.
      </SubCardSubtitle>
      {/* Доказательный архив — §8.5. Здесь же, потому что вопрос один:
          что у нас есть по этому документу и чем это подтверждается. */}
      <EvidenceBlock>
        <EvidenceTitle>Документы по перевозке</EvidenceTitle>
        {evidence ? (
          <EvidenceContent>
            Комплект сохранён{' '}
            {new Date(evidence.fetched_at).toLocaleString('ru-RU')} ·{' '}
            {(evidence.size_bytes / 1024 / 1024).toFixed(2)} МБ
            <EvidenceMonoRow>
              <Mono>SHA-256: {evidence.sha256}</Mono>
            </EvidenceMonoRow>
            <ActionRow>
              <SmallBtn
                as="a"
                href={`/api/v1/agents/logist/etrn/${titlesFor}/evidence/download`}
              >
                Скачать комплект (ZIP)
              </SmallBtn>
              <SmallBtn as="a" href={`/api/v1/agents/logist/etrn/${titlesFor}/print-form`}>
                Печатная форма (PDF)
              </SmallBtn>
              <SmallBtn as="a" href={`/api/v1/agents/logist/etrn/${titlesFor}/qr`}>
                QR для проверки
              </SmallBtn>
              {can_write && (
                <SmallBtn disabled={archiving} onClick={() => onArchive(titlesFor)}>
                  Обновить комплект
                </SmallBtn>
              )}
            </ActionRow>
          </EvidenceContent>
        ) : (
          <EvidenceContent $faded>
            Комплект ещё не сохранён. Он выгружается сам, когда перевозка закрыта; для
            закрытой перевозки можно забрать его вручную.
            {can_write && (
              <ActionRow>
                <SmallBtn
                  disabled={!enabled || archiving}
                  onClick={() => onArchive(titlesFor)}
                >
                  {archiving ? 'Выгружаем…' : 'Заархивировать'}
                </SmallBtn>
              </ActionRow>
            )}
          </EvidenceContent>
        )}
      </EvidenceBlock>

      {titlesLoading ? (
        <EmptyState>Загрузка…</EmptyState>
      ) : (titles ?? []).length === 0 ? (
        <EmptyState>
          Частей пока нет. Они появятся после того, как документ подпишут у оператора —
          данные подтянутся ближайшей сверкой статуса.
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Часть документа</Th>
              <Th>Состояние</Th>
              <Th>Создана</Th>
            </tr>
          </thead>
          <tbody>
            {(titles ?? []).map((t) => (
              <tr key={t.id}>
                <Td>
                  {/* Перечень типов ведёт оператор: незнакомый показываем
                      как есть, чтобы новый титул не исчезал из витрины. */}
                  {LOGIST_TITLE_TYPE_LABELS[t.title_type] ?? t.title_type}
                </Td>
                <Td>
                  <Badge
                    $tone={
                      t.signature_rejected || t.status === 'InvalidSignature'
                        ? 'bad'
                        : t.status === 'Signed'
                          ? 'ok'
                          : 'warn'
                    }
                  >
                    {t.signature_rejected
                      ? 'Подпись отклонена'
                      : (LOGIST_TITLE_STATUS_LABELS[t.status ?? ''] ?? t.status ?? '—')}
                  </Badge>
                </Td>
                <Td>
                  {t.created_at_op
                    ? new Date(t.created_at_op).toLocaleString('ru-RU')
                    : '—'}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </SubCard>
  );
}
